import React, { useEffect, useState } from 'react';
import { Search, X, ChevronDown, ChevronLeft, ChevronRight, ExternalLink, Building2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface AdvancedSearchProps {
  onResultClick: () => void;
}
export function AdvancedSearch({
  onResultClick
}: AdvancedSearchProps) {
  const [companyName, setCompanyName] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalStage, setModalStage] = useState<'connecting' | 'matched' | 'layering' | 'ready'>('connecting');
  const [foundItems, setFoundItems] = useState<number>(0);
  const handleViewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(true);
    setModalStage('connecting');
    setFoundItems(0);
    // After 1.5 seconds, show entity matched
    setTimeout(() => {
      setModalStage('matched');
      // After 1.5 seconds, show layering additional sources
      setTimeout(() => {
        setModalStage('layering');
        // Cascade through finding each item
        const dataItems = 4;
        for (let i = 1; i <= dataItems; i++) {
          setTimeout(() => {
            setFoundItems(i);
          }, i * 400); // 400ms between each checkmark
        }
        // After all items are found (1.6s) + brief pause (800ms), show ready
        setTimeout(() => {
          setModalStage('ready');
          // After 1.5 seconds, close modal and navigate
          setTimeout(() => {
            setShowModal(false);
            onResultClick();
          }, 1500);
        }, dataItems * 400 + 800);
      }, 1500);
    }, 1500);
  };
  useEffect(() => {
    const targetText = 'Dunder Mifflin';
    let currentIndex = 0;
    // Start typing after a short delay
    const startDelay = setTimeout(() => {
      const typingInterval = setInterval(() => {
        if (currentIndex <= targetText.length) {
          setCompanyName(targetText.slice(0, currentIndex));
          currentIndex++;
        } else {
          clearInterval(typingInterval);
          // Trigger search after typing finishes
          setTimeout(() => {
            setIsSearching(true);
            setTimeout(() => {
              setIsSearching(false);
              setShowResults(true);
            }, 800);
          }, 400);
        }
      }, 80); // Typing speed
      return () => clearInterval(typingInterval);
    }, 500);
    return () => clearTimeout(startDelay);
  }, []);
  return <div className="px-8 py-6 max-w-6xl mx-auto w-full" data-id="element-262">
      <h1 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-263">
        Advanced Company Search
      </h1>

      <div className="bg-white rounded-lg border border-gray-200 shadow-sm mb-6" data-id="element-264">
        <div className="p-6 space-y-4" data-id="element-265">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-266">
            {/* Company Name */}
            <div className="relative" data-id="element-267">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-indigo-600 z-10" data-id="element-268">
                Company Name *
              </label>
              <div className="relative" data-id="element-269">
                <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full px-4 py-3 border-2 border-indigo-500 rounded-lg focus:outline-none text-gray-900" data-id="element-270" />
                {companyName && <button onClick={() => setCompanyName('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-271">
                    <X className="w-5 h-5" data-id="element-272" />
                  </button>}
              </div>
            </div>

            {/* Company Website */}
            <div data-id="element-273">
              <input type="text" placeholder="Company Website" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-274" />
            </div>

            {/* Country */}
            <div className="relative" data-id="element-275">
              <label className="absolute -top-2 left-3 bg-white px-1 text-xs font-medium text-gray-500 z-10" data-id="element-276">
                Country
              </label>
              <div className="relative" data-id="element-277">
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-278">
                  <option data-id="element-279">United States</option>
                  <option data-id="element-280">Canada</option>
                  <option data-id="element-281">United Kingdom</option>
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1 text-gray-400 pointer-events-none" data-id="element-282">
                  <X className="w-4 h-4" data-id="element-283" />
                  <ChevronDown className="w-4 h-4" data-id="element-284" />
                </div>
              </div>
            </div>

            {/* State */}
            <div className="relative" data-id="element-285">
              <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 appearance-none bg-white" data-id="element-286">
                <option value="" disabled selected hidden data-id="element-287">
                  State
                </option>
                <option data-id="element-288">Pennsylvania</option>
                <option data-id="element-289">Connecticut</option>
                <option data-id="element-290">New York</option>
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-291" />
            </div>
          </div>

          {/* Street Address */}
          <div className="relative" data-id="element-292">
            <input type="text" placeholder="Street Address" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-293" />
            <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-294" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-295">
            {/* City */}
            <div className="relative" data-id="element-296">
              <input type="text" placeholder="City" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-297" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-298" />
            </div>

            {/* Postal Code */}
            <div className="relative" data-id="element-299">
              <input type="text" placeholder="Postal Code" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-gray-900 placeholder-gray-500" data-id="element-300" />
              <Building2 className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-300 pointer-events-none" data-id="element-301" />
            </div>
          </div>

          {/* Search Button */}
          <button className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-3 rounded-lg flex items-center justify-center gap-2 transition-colors mt-2" disabled={isSearching} data-id="element-302">
            {isSearching ? <motion.div animate={{
            rotate: 360
          }} transition={{
            duration: 1,
            repeat: Infinity,
            ease: 'linear'
          }} className="w-5 h-5 border-2 border-white border-t-transparent rounded-full" data-id="element-303" /> : <>
                <Search className="w-5 h-5" data-id="element-304" />
                SEARCH
              </>}
          </button>
        </div>

        {/* Help Section */}
        <div className="border-t border-gray-200 px-6 py-4 flex items-center justify-between text-sm" data-id="element-305">
          <div className="text-gray-600" data-id="element-306">
            Need help finding a company?{' '}
            <a href="#" className="text-indigo-600 hover:underline" data-id="element-307">
              Contact search support
            </a>
          </div>
          <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-308" />
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence data-id="element-309">
        {showResults && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} className="space-y-4" data-id="element-310">
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-6 py-3 flex items-center justify-between" data-id="element-311">
              <span className="text-gray-700" data-id="element-312">Showing results 1 to 2</span>
              <div className="flex items-center gap-2" data-id="element-313">
                <button className="px-3 py-1.5 text-sm text-gray-400 flex items-center gap-1 cursor-not-allowed" data-id="element-314">
                  <ChevronLeft className="w-4 h-4" data-id="element-315" />
                  PREVIOUS PAGE
                </button>
                <button className="px-3 py-1.5 text-sm text-indigo-600 border border-indigo-200 rounded flex items-center gap-1 hover:bg-indigo-50" data-id="element-316">
                  NEXT PAGE
                  <ChevronRight className="w-4 h-4" data-id="element-317" />
                </button>
              </div>
            </div>

            {/* Result 1 - HQ (Clickable) */}
            <motion.div whileHover={{
          scale: 1.005
        }} onClick={handleViewClick} className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 cursor-pointer hover:border-indigo-300 hover:bg-indigo-100 hover:shadow-md transition-all relative overflow-hidden group" data-id="element-318">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-319" />
              <div className="pl-4" data-id="element-320">
                <div className="flex items-center gap-2 mb-1" data-id="element-321">
                  <h3 className="text-lg font-medium text-gray-900 group-hover:text-indigo-600 transition-colors" data-id="element-322">
                    DUNDER MIFFLIN PAPER COMPANY
                  </h3>
                  <ExternalLink className="w-4 h-4 text-indigo-500 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-323" />
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-324">
                  1725 SLOUGH AVENUE, SCRANTON, PA, 18505
                </p>
                <div className="flex items-center justify-between" data-id="element-325">
                  <div className="flex items-center gap-2" data-id="element-326">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-327">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-328">
                      HQ
                    </span>
                  </div>
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={handleViewClick} className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg transition-colors" data-id="element-329">
                    VIEW
                  </motion.button>
                </div>
              </div>
            </motion.div>

            {/* Result 2 - Branch */}
            <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 relative overflow-hidden hover:bg-indigo-50/30 transition-all" data-id="element-330">
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-green-500" data-id="element-331" />
              <div className="pl-4" data-id="element-332">
                <div className="flex items-center gap-2 mb-1" data-id="element-333">
                  <h3 className="text-lg font-medium text-gray-900" data-id="element-334">
                    DUNDER MIFFLIN STAMFORD
                  </h3>
                </div>
                <p className="text-indigo-600 text-sm mb-3" data-id="element-335">
                  850 CANAL ST, STAMFORD, CT, 06902
                </p>
                <div className="flex items-center justify-between" data-id="element-336">
                  <div className="flex items-center gap-2" data-id="element-337">
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-338">
                      Active
                    </span>
                    <span className="px-2.5 py-1 text-xs font-medium text-gray-700 bg-gray-100 border border-gray-200 rounded-full" data-id="element-339">
                      Branch
                    </span>
                  </div>
                  <motion.button whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} onClick={onResultClick} className="px-6 py-2.5 bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 text-sm font-semibold rounded-lg transition-colors" data-id="element-340">
                    ADD
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Data Discovery Modal */}
      <AnimatePresence data-id="element-341">
        {showModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black/50 z-50" data-id="element-342" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-343">
              <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-8" data-id="element-344">
                {modalStage === 'connecting' && <div className="text-center" data-id="element-345">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-346" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-347">
                      Connecting to your preferred bureau
                    </h3>
                    <p className="text-gray-600" data-id="element-348">Searching for company...</p>
                  </div>}

                {modalStage === 'matched' && <div className="text-center" data-id="element-349">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-350">
                      <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-351">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-352" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-353">
                      Entity Matched
                    </h3>
                    <p className="text-gray-600" data-id="element-354">
                      Dunder Mifflin Paper Company verified
                    </p>
                  </div>}

                {modalStage === 'layering' && <div className="text-center" data-id="element-355">
                    <motion.div animate={{
                rotate: 360
              }} transition={{
                duration: 1,
                repeat: Infinity,
                ease: 'linear'
              }} className="w-16 h-16 border-4 border-indigo-200 border-t-indigo-600 rounded-full mx-auto mb-6" data-id="element-356" />
                    <h3 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-357">
                      Layering additional data sources
                    </h3>
                    <p className="text-sm text-gray-500 mb-4" data-id="element-358">
                      Enriching bureau data with:
                    </p>
                    <div className="space-y-3 text-left" data-id="element-359">
                      {['People', 'Private Financials', 'Recent News', 'Social & Web Presence'].map((item, index) => <motion.div key={item} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: 1,
                  x: 0
                }} transition={{
                  delay: index * 0.2
                }} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg" data-id="element-360">
                          {foundItems > index ? <motion.div initial={{
                    scale: 0
                  }} animate={{
                    scale: 1
                  }} transition={{
                    type: 'spring',
                    stiffness: 500,
                    damping: 30
                  }} className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-361">
                              <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3} data-id="element-362">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-363" />
                              </svg>
                            </motion.div> : <div className="w-2 h-2 bg-indigo-600 rounded-full" data-id="element-364" />}
                          <span className={`font-medium transition-colors ${foundItems > index ? 'text-gray-900' : 'text-gray-600'}`} data-id="element-365">
                            {item}
                          </span>
                        </motion.div>)}
                    </div>
                  </div>}

                {modalStage === 'ready' && <div className="text-center" data-id="element-366">
                    <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-367">
                      <svg className="w-8 h-8 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-368">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-369" />
                      </svg>
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-370">
                      Report Ready
                    </h3>
                    <p className="text-gray-600" data-id="element-371">Opening account view...</p>
                  </div>}
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}